import Vue from 'vue';
import Vuex from 'vuex';
import allsettled from 'promise.allsettled';
import CmgApp from './views/App/CmgApp.vue';
import VuePortal from '@linusborg/vue-simple-portal';

import router from './router';
import { buildConfig } from './environment';
import { buildStore, AppStorePlugin, AppState } from './store';
import { buildServicesContainer, ServicesPlugin, ServicesContainer } from './services';
import { registerAllCmgComponents } from './components';
import { AnalyticsEventCategory, AnalyticsEventAction, GoogleAnalyticsService } from './services/google/google-analytics.services';
import { initViceNoscriptElement } from './helpers/util';
import { setupI18n } from './i18n';

import './directives';
import './filters';

// Turning this off until it's ready to be used
// import './registerServiceWorker';

if (process.env.VUE_APP_IS_LOCAL) {
  require('@/assets/style/_fonts-local.scss');
} else {
  require('@/assets/style/_fonts.scss');
}

import '@/assets/style/_global.scss';
import AdobeAnalyticsListeners from './services/analytics/platforms/adobe-analytics/adobe-analytics-listeners';
import { adobeEventEmitter } from './services/analytics/analytics.service';

allsettled.shim();

function sendInitialGAEvents(googleAnalyticsService: GoogleAnalyticsService, storeState: AppState) {
  const restaurantNumber = storeState.restaurant.selectedRestaurant?.restaurantNumber;
  if (restaurantNumber) {
    googleAnalyticsService.sendCustomEvent({
      category: AnalyticsEventCategory.Restaurant,
      action: AnalyticsEventAction.RestaurantUpdate,
      label: restaurantNumber.toString(),
    });
  }
}

async function init() {
  let services: ServicesContainer;

  router.beforeEach((to, from, next) => {
    if (to?.meta?.title) {
      document.title = to.meta?.title;
    }
    next();
    services.googleAnalytics.sendPageViewEvent(to.path);
    services.googleAnalytics.resetLastScrollPercentage();
  });

  const config = await buildConfig();

  // Add Plugins
  Vue.use(Vuex);
  Vue.use(AppStorePlugin);
  Vue.use(ServicesPlugin);
  Vue.use(VuePortal);
  const i18n = setupI18n();

  const store = buildStore(config);
  services = buildServicesContainer(store, config, router);

  AdobeAnalyticsListeners.register(adobeEventEmitter, store, services);

  registerAllCmgComponents(services.logger.newInstance('Component Registration'));

  // Setting up callbacks for adobe target responses
  window.targetData = window.targetData || [] as any;
  window.__alloyMonitors = window.__alloyMonitors || [] as any;
  window.__alloyMonitors?.push({
    onNetworkResponse(data) {
      services.adobeTarget.processAdobeTargetNetworkResponse(data);
    }
  });

  sendInitialGAEvents(services.googleAnalytics, store.state);
  initViceNoscriptElement();

  if (config.isLocal) {
    Vue.config.performance = true;

    (window as any).store = store;
    (window as any).services = services;
  }

  new Vue({
    router,
    store,
    services,
    i18n,
    render: h => h(CmgApp),
  }).$mount('#app');

}

// Prevent app.js from firing twice, if the script is loaded twice on the page
// Which happens a lot due to AEM being dumb
if (!(window as any)._cmgIsAppJsInitialized) {
  (window as any)._cmgIsAppJsInitialized = true;
  init();
} else {
  console.warn('Detecting two app.js scripts. Please ensure only one app.js script loads on the page.');
}
